/**
 * QuestionFlow.jsx
 * Progressive expand: each question auto-collapses into a summary pill
 * when answered, active question expanded, locked questions greyed out.
 * Replaces WizardStep.jsx.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Slider,
  Chip,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  LockOutlined as LockIcon,
  CheckCircle as CheckIcon,
  EditOutlined as EditIcon,
  InfoOutlined as InfoIcon,
  TipsAndUpdatesOutlined as TipIcon,
  WarningAmberOutlined as WarningIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getInsights } from './insightEngine';
import { trackInsightShown } from './analytics';
import { getMortgageInsuranceRate, formatCurrencyShort, calculateMinimumDownPayment } from '../mortgageCalcEngine';
import SmartProvinceSelector from '../../shared/SmartProvinceSelector';

/* ── Province labels for summary ── */
const PROVINCE_LABELS = {
  ON: 'Ontario', QC: 'Quebec', BC: 'BC', AB: 'Alberta',
  MB: 'Manitoba', SK: 'Saskatchewan', NS: 'Nova Scotia', NB: 'New Brunswick',
  NL: 'NL', PE: 'PEI', NT: 'NT', NU: 'NU', YT: 'YT',
};

const STATUS_OPTIONS = [
  { key: 'pr', emoji: '🍁' },
  { key: 'citizen', emoji: '🇨🇦' },
  { key: 'work_permit', emoji: '💼' },
  { key: 'student', emoji: '🎓' },
  { key: 'newcomer_2y', emoji: '🌱' },
  { key: 'other', emoji: '📋' },
];
const CREDIT_OPTIONS = [
  { key: 'yes', emoji: '✅' },
  { key: 'no', emoji: '❌' },
  { key: 'unsure', emoji: '🤔' },
];
const REFI_GOALS = [
  { key: 'lower_payment', icon: '💰' },
  { key: 'pull_equity', icon: '🏠' },
  { key: 'switch_term', icon: '🔄' },
];

/* ── Insight bubble ── */
const InsightBubble = ({ insights }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    setVisible(true);
    // Track shown insights
    insights.forEach((i) => trackInsightShown(i.key));
    timerRef.current = setTimeout(() => setVisible(false), 6000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [insights]);

  if (!visible || insights.length === 0) return null;

  const iconMap = {
    info: <InfoIcon sx={{ fontSize: 16, color: '#1E3A5F', flexShrink: 0 }} />,
    tip: <TipIcon sx={{ fontSize: 16, color: '#F9A825', flexShrink: 0 }} />,
    warning: <WarningIcon sx={{ fontSize: 16, color: '#C62828', flexShrink: 0 }} />,
  };

  const bgMap = {
    info: 'rgba(30,58,95,0.06)',
    tip: 'rgba(249,168,37,0.08)',
    warning: 'rgba(198,40,40,0.06)',
  };

  // Show only the most recent insight
  const insight = insights[insights.length - 1];

  return (
    <Box
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1,
          p: 1.5, borderRadius: '10px', mt: 1.5,
          bgcolor: bgMap[insight.type] || bgMap.info,
          animation: 'fadeIn 0.3s ease',
          '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
        }}
      >
        {iconMap[insight.type] || iconMap.info}
        <Typography sx={{ fontSize: '13px', color: '#1A1A1A', lineHeight: 1.5 }}>
          {t(`mortgageEngine.insights.${insight.key}`)}
        </Typography>
      </Box>
  );
};

/* ── Selectable pill (Province) ── */
const ProvincePills = ({ value, onChange }) => (
  <SmartProvinceSelector
    value={value}
    onSelect={(code) => onChange('province', code)}
  />
);

/* ── Price slider + DP toggle + CMHC meter ── */
const PropertyControls = ({ answers, onChange }) => {
  const { t } = useTranslation();
  const [dpMode, setDpMode] = useState(answers.dpMode || 'percent');

  const dpPercent = answers.price > 0 ? (answers.downPayment / answers.price) * 100 : 5;
  const cmhcRate = getMortgageInsuranceRate(dpPercent);
  const minDp = calculateMinimumDownPayment(answers.price);

  const handlePriceChange = (_, val) => {
    onChange('price', val);
    // Keep DP as same percentage
    const newDp = Math.round(val * (dpPercent / 100));
    const clampedDp = Math.max(newDp, calculateMinimumDownPayment(val));
    onChange('downPayment', clampedDp);
  };

  const handleDpPercentChange = (_, val) => {
    const newDp = Math.round(answers.price * (val / 100));
    onChange('downPayment', newDp);
  };

  const handleDpDollarChange = (_, val) => {
    onChange('downPayment', val);
  };

  const handleDpModeToggle = (_, newMode) => {
    if (newMode) {
      setDpMode(newMode);
      onChange('dpMode', newMode);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Price slider */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A' }}>
            {t('mortgageWizard.fields.price')}
          </Typography>
          <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1E3A5F', fontFeatureSettings: "'tnum'" }}>
            {formatCurrencyShort(answers.price)}
          </Typography>
        </Box>
        <Slider
          value={answers.price}
          onChange={handlePriceChange}
          min={100000}
          max={2000000}
          step={25000}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => formatCurrencyShort(v)}
          sx={{
            color: '#1E3A5F',
            '& .MuiSlider-thumb': {
              width: 22, height: 22, bgcolor: '#FFFFFF', border: '2px solid #1E3A5F',
              boxShadow: '0 2px 6px rgba(30,58,95,0.18)',
              '&.Mui-active, &:hover': { boxShadow: '0 4px 14px rgba(30,58,95,0.25)' },
            },
            '& .MuiSlider-rail': { bgcolor: '#E6E8EC', height: 8, borderRadius: 4 },
            '& .MuiSlider-track': { height: 8, borderRadius: 4 },
            '& .MuiSlider-valueLabel': { bgcolor: '#1E3A5F', borderRadius: '8px', fontSize: '12px', fontWeight: 600 },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>$100K</Typography>
          <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>$2M</Typography>
        </Box>
      </Box>

      {/* DP mode toggle */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A' }}>
            {t('mortgageWizard.fields.downPayment')}
          </Typography>
          <ToggleButtonGroup
            value={dpMode}
            exclusive
            onChange={handleDpModeToggle}
            size="small"
            sx={{ height: { xs: 36, sm: 28 } }}
          >
            <ToggleButton
              value="percent"
              sx={{
                fontSize: '13px', textTransform: 'none', px: 2, py: 0,
                minWidth: 44, minHeight: { xs: 36, sm: 28 },
                borderColor: '#E9EDF3', borderRadius: '8px',
                transition: 'all 0.2s ease',
                '&.Mui-selected': { bgcolor: '#1E3A5F', color: '#FFFFFF', borderColor: '#1E3A5F', boxShadow: '0 2px 6px rgba(30,58,95,0.15)', '&:hover': { bgcolor: '#1A3152' } },
              }}
            >
              %
            </ToggleButton>
            <ToggleButton
              value="dollar"
              sx={{
                fontSize: '13px', textTransform: 'none', px: 2, py: 0,
                minWidth: 44, minHeight: { xs: 36, sm: 28 },
                borderColor: '#E9EDF3', borderRadius: '8px',
                transition: 'all 0.2s ease',
                '&.Mui-selected': { bgcolor: '#1E3A5F', color: '#FFFFFF', borderColor: '#1E3A5F', boxShadow: '0 2px 6px rgba(30,58,95,0.15)', '&:hover': { bgcolor: '#1A3152' } },
              }}
            >
              $
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* DP value display */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1E3A5F', fontFeatureSettings: "'tnum'" }}>
            {dpMode === 'percent' ? `${dpPercent.toFixed(1)}%` : formatCurrencyShort(answers.downPayment)}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>
            {dpMode === 'percent' ? formatCurrencyShort(answers.downPayment) : `${dpPercent.toFixed(1)}%`}
          </Typography>
        </Box>

        {dpMode === 'percent' ? (
          <Slider
            value={dpPercent}
            onChange={handleDpPercentChange}
            min={5}
            max={50}
            step={1}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v.toFixed(0)}%`}
            sx={{
              color: dpPercent >= 20 ? '#2E7D32' : '#F9A825',
              '& .MuiSlider-thumb': {
                width: 22, height: 22, bgcolor: '#FFFFFF', border: '2px solid currentColor',
                boxShadow: '0 2px 6px rgba(30,58,95,0.18)',
                '&.Mui-active, &:hover': { boxShadow: '0 4px 14px rgba(30,58,95,0.25)' },
              },
              '& .MuiSlider-rail': { bgcolor: '#E6E8EC', height: 8, borderRadius: 4 },
              '& .MuiSlider-track': { height: 8, borderRadius: 4 },
              '& .MuiSlider-valueLabel': { bgcolor: '#1E3A5F', borderRadius: '8px', fontSize: '12px', fontWeight: 600 },
            }}
          />
        ) : (
          <Slider
            value={answers.downPayment}
            onChange={handleDpDollarChange}
            min={Math.round(minDp)}
            max={Math.round(answers.price * 0.5)}
            step={5000}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => formatCurrencyShort(v)}
            sx={{
              color: dpPercent >= 20 ? '#2E7D32' : '#F9A825',
              '& .MuiSlider-thumb': {
                width: 22, height: 22, bgcolor: '#FFFFFF', border: '2px solid currentColor',
                boxShadow: '0 2px 6px rgba(30,58,95,0.18)',
                '&.Mui-active, &:hover': { boxShadow: '0 4px 14px rgba(30,58,95,0.25)' },
              },
              '& .MuiSlider-rail': { bgcolor: '#E6E8EC', height: 8, borderRadius: 4 },
              '& .MuiSlider-track': { height: 8, borderRadius: 4 },
              '& .MuiSlider-valueLabel': { bgcolor: '#1E3A5F', borderRadius: '8px', fontSize: '12px', fontWeight: 600 },
            }}
          />
        )}
      </Box>

      {/* CMHC meter */}
      <Tooltip
        title={
          dpPercent >= 20
            ? t('mortgageEngine.preview.cmhcAdvisory_none')
            : dpPercent >= 10
              ? t('mortgageEngine.preview.cmhcAdvisory_low', { dp: dpPercent.toFixed(0), rate: cmhcRate })
              : t('mortgageEngine.preview.cmhcAdvisory_high', { dp: dpPercent.toFixed(0), rate: cmhcRate })
        }
        arrow
        placement="top"
      >
        <Box
          sx={{
            p: 1.5, borderRadius: '10px', cursor: 'help',
            bgcolor: cmhcRate > 0 ? 'rgba(249,168,37,0.08)' : 'rgba(46,125,50,0.06)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1A1A1A' }}>
              {t('mortgageEngine.preview.cmhcLabel')}
            </Typography>
            <Chip
              size="small"
              label={cmhcRate > 0 ? `${cmhcRate}%` : t('mortgageEngine.preview.cmhcNo')}
              sx={{
                height: 20, fontSize: '12px', fontWeight: 600,
                bgcolor: cmhcRate > 0 ? '#FFF8E1' : '#E8F5E9',
                color: cmhcRate > 0 ? '#C68A00' : '#2E7D32',
              }}
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={dpPercent >= 20 ? 100 : (dpPercent / 20) * 100}
            sx={{
              height: 8, borderRadius: 4,
              bgcolor: '#E6E8EC',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                background: dpPercent >= 20
                  ? 'linear-gradient(90deg, #66BB6A 0%, #2E7D32 100%)'
                  : `linear-gradient(90deg, #F9A825 0%, ${dpPercent >= 10 ? '#66BB6A' : '#EF6C00'} 100%)`,
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>5%</Typography>
            <Typography sx={{ fontSize: '12px', color: '#5F6B7A', fontWeight: dpPercent >= 20 ? 700 : 400 }}>20%</Typography>
          </Box>
        </Box>
      </Tooltip>
    </Box>
  );
};

/* ── Selectable cards for status ── */
const SelectableCards = ({ options, value, onChange, field, columns = 3 }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: `repeat(${columns}, 1fr)` }, gap: { xs: 1, sm: 1.5 } }}>
      {options.map(({ key, emoji }) => {
        const selected = value === key;
        return (
          <Box
            key={key}
            onClick={() => onChange(field, key)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(field, key); } }}
            sx={{
              p: { xs: 1.5, sm: 2 }, borderRadius: '12px', cursor: 'pointer', textAlign: 'center',
              border: selected ? '1.5px solid #1E3A5F' : '1px solid #EEF0F3',
              bgcolor: selected ? '#F0F4FA' : '#FFFFFF',
              boxShadow: selected ? '0 3px 12px rgba(30,58,95,0.14)' : '0 1px 4px rgba(0,0,0,0.04)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              '&:hover': {
                borderColor: selected ? '#1E3A5F' : '#C8D0DC',
                boxShadow: '0 4px 14px rgba(30,58,95,0.10)',
                transform: 'translateY(-1px)',
                bgcolor: selected ? '#F0F4FA' : '#FAFBFD',
              },
              '&:active': { transform: 'scale(0.97)', boxShadow: '0 1px 4px rgba(30,58,95,0.10)' },
              '&:focus-visible': { outline: '2px solid #1E3A5F', outlineOffset: 2 },
            }}
          >
            <Box sx={{ fontSize: { xs: '20px', sm: '24px' }, lineHeight: 1, mb: 0.75 }}>{emoji}</Box>
            <Typography sx={{ fontSize: { xs: '12px', sm: '13px' }, fontWeight: selected ? 700 : 500, color: selected ? '#1E3A5F' : '#1A1A1A', lineHeight: 1.3 }}>
              {t(`mortgageWizard.fields.statusOptions.${key}`, { defaultValue: key })}
            </Typography>
            {/* Selected checkmark */}
            {selected && (
              <CheckIcon sx={{
                position: 'absolute', top: 6, right: 6, fontSize: 16, color: '#1E3A5F',
              }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

/* ── Credit toggle cards ── */
const CreditToggle = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 } }}>
      {CREDIT_OPTIONS.map(({ key, emoji }) => {
        const selected = value === key;
        return (
          <Box
            key={key}
            onClick={() => onChange('credit', key)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange('credit', key); } }}
            sx={{
              flex: 1, py: { xs: 1.25, sm: 1.5 }, px: { xs: 1, sm: 2 },
              borderRadius: '12px', cursor: 'pointer', textAlign: 'center',
              border: selected ? '1.5px solid #1E3A5F' : '1px solid #EEF0F3',
              bgcolor: selected ? '#F0F4FA' : '#FFFFFF',
              boxShadow: selected ? '0 3px 12px rgba(30,58,95,0.14)' : '0 1px 4px rgba(0,0,0,0.04)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: selected ? '#1E3A5F' : '#C8D0DC',
                boxShadow: '0 4px 14px rgba(30,58,95,0.10)',
                transform: 'translateY(-1px)',
              },
              '&:active': { transform: 'scale(0.97)' },
              '&:focus-visible': { outline: '2px solid #1E3A5F', outlineOffset: 2 },
            }}
          >
            <Box sx={{ fontSize: { xs: '18px', sm: '20px' }, lineHeight: 1, mb: 0.5 }}>{emoji}</Box>
            <Typography sx={{ fontSize: { xs: '12px', sm: '13px' }, fontWeight: selected ? 700 : 600, color: selected ? '#1E3A5F' : '#1A1A1A' }}>
              {t(`mortgageWizard.fields.creditOptions.${key}`)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

/* ── Income slider ── */
const IncomeSlider = ({ value, onChange }) => {
  const { t } = useTranslation();
  const displayValue = value || 0;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A' }}>
          {t('mortgageWizard.fields.income')}
        </Typography>
        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1E3A5F', fontFeatureSettings: "'tnum'" }}>
          {displayValue > 0 ? formatCurrencyShort(displayValue) : '—'}
        </Typography>
      </Box>
      <Slider
        value={displayValue}
        onChange={(_, v) => onChange('income', v)}
        min={0}
        max={300000}
        step={5000}
        valueLabelDisplay="auto"
        valueLabelFormat={(v) => v > 0 ? formatCurrencyShort(v) : '—'}
        sx={{
          color: '#1E3A5F',
          '& .MuiSlider-thumb': {
            width: 22, height: 22, bgcolor: '#FFFFFF', border: '2px solid #1E3A5F',
            boxShadow: '0 2px 6px rgba(30,58,95,0.18)',
            '&.Mui-active, &:hover': { boxShadow: '0 4px 14px rgba(30,58,95,0.25)' },
          },
          '& .MuiSlider-rail': { bgcolor: '#E6E8EC', height: 8, borderRadius: 4 },
          '& .MuiSlider-track': { height: 8, borderRadius: 4 },
          '& .MuiSlider-valueLabel': { bgcolor: '#1E3A5F', borderRadius: '8px', fontSize: '12px', fontWeight: 600 },
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>$0</Typography>
        <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>$300K</Typography>
      </Box>
      <Typography sx={{ fontSize: '12px', color: '#5F6B7A', mt: 0.5 }}>
        {t('mortgageWizard.fields.incomeOptional')}
      </Typography>
    </Box>
  );
};

/* ── Offer accepted toggle ── */
const OfferToggle = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', mb: 1 }}>
        {t('mortgageWizard.fields.offerAccepted')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        {[true, false].map((opt) => {
          const selected = value === opt;
          return (
            <Box
              key={String(opt)}
              onClick={() => onChange('offerAccepted', opt)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange('offerAccepted', opt); } }}
              sx={{
                px: 3, py: 1.5, borderRadius: '10px', cursor: 'pointer', textAlign: 'center',
                border: selected ? '1.5px solid #1E3A5F' : '1px solid transparent',
                bgcolor: selected ? '#F7F9FC' : '#FFFFFF',
                boxShadow: selected ? '0 2px 8px rgba(30,58,95,0.14)' : '0 1px 4px rgba(0,0,0,0.06)',
                transition: 'all 0.2s ease',
                '&:hover': { boxShadow: '0 3px 10px rgba(30,58,95,0.10)' },
              }}
            >
              <Typography sx={{ fontSize: '14px', fontWeight: selected ? 700 : 500, color: '#1A1A1A' }}>
                {opt ? t('mortgageWizard.fields.creditOptions.yes') : t('mortgageWizard.fields.creditOptions.no')}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/* ── Refi balance slider ── */
const BalanceSlider = ({ answers, onChange }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A' }}>
            {t('mortgageWizard.fields.balance')}
          </Typography>
          <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1E3A5F', fontFeatureSettings: "'tnum'" }}>
            {formatCurrencyShort(answers.balance || 0)}
          </Typography>
        </Box>
        <Slider
          value={answers.balance || 100000}
          onChange={(_, v) => onChange('balance', v)}
          min={50000}
          max={1500000}
          step={25000}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => formatCurrencyShort(v)}
          sx={{
            color: '#1E3A5F',
            '& .MuiSlider-thumb': {
              width: 22, height: 22, bgcolor: '#FFFFFF', border: '2px solid #1E3A5F',
              boxShadow: '0 2px 6px rgba(30,58,95,0.18)',
              '&.Mui-active, &:hover': { boxShadow: '0 4px 14px rgba(30,58,95,0.25)' },
            },
            '& .MuiSlider-rail': { bgcolor: '#E6E8EC', height: 8, borderRadius: 4 },
            '& .MuiSlider-track': { height: 8, borderRadius: 4 },
            '& .MuiSlider-valueLabel': { bgcolor: '#1E3A5F', borderRadius: '8px', fontSize: '12px', fontWeight: 600 },
          }}
        />
      </Box>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A' }}>
            {t('mortgageWizard.fields.homeValue')}
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#5F6B7A', fontFeatureSettings: "'tnum'" }}>
            {answers.homeValue ? formatCurrencyShort(answers.homeValue) : '—'}
          </Typography>
        </Box>
        <Slider
          value={answers.homeValue || 0}
          onChange={(_, v) => onChange('homeValue', v)}
          min={0}
          max={2000000}
          step={25000}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => v > 0 ? formatCurrencyShort(v) : '—'}
          sx={{
            color: '#5F6B7A',
            '& .MuiSlider-thumb': {
              width: 22, height: 22, bgcolor: '#FFFFFF', border: '2px solid #5F6B7A',
              boxShadow: '0 2px 6px rgba(95,107,122,0.18)',
              '&.Mui-active, &:hover': { boxShadow: '0 4px 14px rgba(95,107,122,0.25)' },
            },
            '& .MuiSlider-rail': { bgcolor: '#E6E8EC', height: 8, borderRadius: 4 },
            '& .MuiSlider-track': { height: 8, borderRadius: 4 },
            '& .MuiSlider-valueLabel': { bgcolor: '#5F6B7A', borderRadius: '8px', fontSize: '12px', fontWeight: 600 },
          }}
        />
        <Typography sx={{ fontSize: '12px', color: '#5F6B7A', mt: 0.5 }}>
          {t('mortgageWizard.fields.homeValueOptional')}
        </Typography>
      </Box>
    </Box>
  );
};

/* ── Refi goal cards ── */
const GoalCards = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {REFI_GOALS.map(({ key, icon }) => {
        const selected = value === key;
        return (
          <Box
            key={key}
            onClick={() => onChange('refiGoal', key)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange('refiGoal', key); } }}
            sx={{
              p: 2, borderRadius: '12px', cursor: 'pointer',
              border: selected ? '1.5px solid #1E3A5F' : '1px solid transparent',
              bgcolor: selected ? '#F7F9FC' : '#FFFFFF',
              boxShadow: selected ? '0 2px 8px rgba(30,58,95,0.14)' : '0 1px 4px rgba(0,0,0,0.06)',
              transition: 'all 0.2s ease',
              '&:hover': { boxShadow: '0 3px 10px rgba(30,58,95,0.10)', bgcolor: '#F7F9FC' },
            }}
          >
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A' }}>
              {icon} {t(`mortgageWizard.fields.refiGoalOptions.${key}`)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};


/* ──────────────────────────────────────────────────────────── */
/* ── QUESTION DEFINITIONS ── */
/* ──────────────────────────────────────────────────────────── */

const BUY_QUESTIONS = [
  {
    id: 'province',
    autoAdvance: true, // single-click pill → advance immediately
    isComplete: (a) => !!a.province,
    summary: (a) => {
      return PROVINCE_LABELS[a.province] || a.province;
    },
    render: (a, onChange) => <ProvincePills value={a.province} onChange={onChange} />,
  },
  {
    id: 'property',
    autoAdvance: false, // sliders — user decides when done
    isComplete: (a) => a.price >= 100000 && a.downPayment > 0,
    summary: (a, t) => {
      const dp = a.price > 0 ? ((a.downPayment / a.price) * 100).toFixed(0) : 0;
      return `${formatCurrencyShort(a.price)} · ${t('mortgageEngine.collapsed.dp_label', { percent: dp })}`;
    },
    render: (a, onChange) => <PropertyControls answers={a} onChange={onChange} />,
  },
  {
    id: 'residency',
    autoAdvance: false, // multi-field (status cards + credit toggle)
    isComplete: (a) => !!a.status && !!a.credit,
    summary: (a, t) => {
      const parts = [];
      if (a.status) parts.push(t(`mortgageWizard.fields.statusOptions.${a.status}`));
      if (a.credit) parts.push(`Credit: ${t(`mortgageWizard.fields.creditOptions.${a.credit}`)}`);
      return parts.join(' · ');
    },
    render: (a, onChange, t) => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <SelectableCards options={STATUS_OPTIONS} value={a.status} onChange={onChange} field="status" />
        <Box>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', mb: 1 }}>
            {t('mortgageWizard.fields.credit')}
          </Typography>
          <CreditToggle value={a.credit} onChange={onChange} />
        </Box>
      </Box>
    ),
  },
  {
    id: 'income',
    autoAdvance: false, // sliders + optional — never auto-advance
    isComplete: () => true,
    summary: (a, t) => {
      const parts = [];
      if (a.income) parts.push(formatCurrencyShort(a.income));
      if (a.offerAccepted !== null && a.offerAccepted !== undefined) {
        parts.push(a.offerAccepted
          ? `Offer: ${t('mortgageWizard.fields.creditOptions.yes')}`
          : `Offer: ${t('mortgageWizard.fields.creditOptions.no')}`);
      }
      return parts.length > 0 ? parts.join(' · ') : t('mortgageWizard.nav.skip');
    },
    render: (a, onChange) => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <IncomeSlider value={a.income} onChange={onChange} />
        <OfferToggle value={a.offerAccepted} onChange={onChange} />
      </Box>
    ),
  },
];

const REFI_QUESTIONS = [
  {
    id: 'province',
    autoAdvance: true,
    isComplete: (a) => !!a.province,
    summary: (a) => {
      return PROVINCE_LABELS[a.province] || a.province;
    },
    render: (a, onChange) => <ProvincePills value={a.province} onChange={onChange} />,
  },
  {
    id: 'financials',
    autoAdvance: false, // sliders
    isComplete: (a) => (a.balance || 0) >= 50000,
    summary: (a) => {
      const parts = [formatCurrencyShort(a.balance || 0)];
      if (a.homeValue) parts.push(`Value: ${formatCurrencyShort(a.homeValue)}`);
      return parts.join(' · ');
    },
    render: (a, onChange) => <BalanceSlider answers={a} onChange={onChange} />,
  },
  {
    id: 'goal',
    autoAdvance: true, // single-click card → advance
    isComplete: (a) => !!a.refiGoal,
    summary: (a, t) => t(`mortgageWizard.fields.refiGoalOptions.${a.refiGoal}`, { defaultValue: a.refiGoal }),
    render: (a, onChange) => <GoalCards value={a.refiGoal} onChange={onChange} />,
  },
  {
    id: 'creditIncome',
    autoAdvance: false, // multi-field
    isComplete: (a) => !!a.credit,
    summary: (a, t) => {
      const parts = [];
      if (a.credit) parts.push(`Credit: ${t(`mortgageWizard.fields.creditOptions.${a.credit}`)}`);
      if (a.income) parts.push(formatCurrencyShort(a.income));
      return parts.length > 0 ? parts.join(' · ') : t('mortgageWizard.nav.skip');
    },
    render: (a, onChange, t) => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', mb: 1 }}>
            {t('mortgageWizard.fields.credit')}
          </Typography>
          <CreditToggle value={a.credit} onChange={onChange} />
        </Box>
        <IncomeSlider value={a.income} onChange={onChange} />
      </Box>
    ),
  },
];


/* ──────────────────────────────────────────────────────────── */
/* ── MAIN COMPONENT ── */
/* ──────────────────────────────────────────────────────────── */

const QuestionFlow = ({ flow, answers, focusedQuestion, onFocusQuestion, onChange, onAutoSubmit }) => {
  const { t } = useTranslation();
  const questions = flow === 'buy' ? BUY_QUESTIONS : REFI_QUESTIONS;
  const prevInsightsRef = useRef([]);
  const autoAdvanceRef = useRef(null);
  const skipAutoAdvanceRef = useRef(false);

  // Compute which questions are visible (previous is complete)
  const visibleCount = (() => {
    let count = 1; // First question always visible
    for (let i = 0; i < questions.length - 1; i++) {
      if (questions[i].isComplete(answers)) count = i + 2;
      else break;
    }
    return Math.min(count, questions.length);
  })();

  // Auto-advance: when a question becomes complete and it's focused, advance to next
  const handleChange = useCallback((field, value) => {
    onChange(field, value);
  }, [onChange]);

  // Auto-advance ONLY for discrete single-click questions (province, goal)
  useEffect(() => {
    if (!focusedQuestion) return;
    const idx = questions.findIndex((q) => q.id === focusedQuestion);
    if (idx === -1) return;
    const q = questions[idx];

    // Clear any pending auto-advance
    if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current);

    // Only auto-advance for single-click discrete selections
    // Skip once after an edit click so the user can re-pick
    if (q.autoAdvance && q.isComplete(answers)) {
      if (skipAutoAdvanceRef.current) {
        skipAutoAdvanceRef.current = false;
        return;
      }
      autoAdvanceRef.current = setTimeout(() => {
        if (idx < questions.length - 1) {
          onFocusQuestion(questions[idx + 1].id);
        } else {
          onAutoSubmit();
        }
      }, 300);
    }

    return () => { if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current); };
  }, [answers, focusedQuestion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual advance for non-auto-advance questions
  const handleContinue = useCallback(() => {
    const idx = questions.findIndex((q) => q.id === focusedQuestion);
    if (idx === -1) return;
    if (idx < questions.length - 1) {
      onFocusQuestion(questions[idx + 1].id);
    } else {
      onAutoSubmit();
    }
  }, [focusedQuestion, questions, onFocusQuestion, onAutoSubmit]);

  // Insights for current answers
  const currentInsights = getInsights(answers);
  // Only show new insights (compare keys)
  const newInsights = currentInsights.filter(
    (ins) => !prevInsightsRef.current.some((prev) => prev.key === ins.key)
  );
  useEffect(() => {
    prevInsightsRef.current = currentInsights;
  }, [currentInsights]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {questions.map((q, idx) => {
        const isVisible = idx < visibleCount;
        const isFocused = focusedQuestion === q.id;
        const isComplete = q.isComplete(answers);
        const isLocked = !isVisible;

        if (isLocked) {
          // Locked state
          return (
            <Box
              key={q.id}
              sx={{
                p: 2, borderRadius: '12px',
                bgcolor: '#FAFBFD', opacity: 0.35,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LockIcon sx={{ fontSize: 16, color: '#C4C9D2' }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#C4C9D2' }}>
                  {t(`mortgageWizard.steps.${q.id}`)}
                </Typography>
              </Box>
            </Box>
          );
        }

        if (!isFocused && isComplete) {
          // Collapsed pill state
          return (
            <Box
              key={q.id}
              onClick={() => { skipAutoAdvanceRef.current = true; onFocusQuestion(q.id); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); skipAutoAdvanceRef.current = true; onFocusQuestion(q.id); } }}
              sx={{
                p: 2, borderRadius: '12px',
                border: 'none', bgcolor: '#FAFBFD',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                cursor: 'pointer', transition: 'all 0.2s ease',
                '&:hover': { bgcolor: '#F7F9FC', boxShadow: '0 3px 12px rgba(30,58,95,0.08)' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon sx={{ fontSize: 18, color: '#2E7D32' }} />
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A', flex: 1 }}>
                  {q.summary(answers, t)}
                </Typography>
                <EditIcon sx={{ fontSize: 16, color: '#5F6B7A' }} />
              </Box>
            </Box>
          );
        }

        // Expanded state (focused or first uncompleted)
        const isLast = idx === questions.length - 1;
        const showContinue = !q.autoAdvance && q.isComplete(answers);
        const showSkip = isLast;

        return (
          <Box
              key={q.id}
              sx={{
                p: { xs: 2, md: 3 }, borderRadius: '14px',
                border: 'none', bgcolor: '#FFFFFF',
                borderLeft: '3px solid #1E3A5F',
                boxShadow: '0 8px 28px rgba(30,58,95,0.10), -2px 0 8px rgba(30,58,95,0.04)',
                animation: 'fadeIn 0.3s ease',
                '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
              }}
            >
              <Typography
                sx={{ fontSize: '15px', fontWeight: 700, color: '#1A1A1A', mb: 2 }}
              >
                {t(`mortgageWizard.steps.${q.id}`)}
              </Typography>
              {q.render(answers, handleChange, t)}

              {/* Continue button for slider/multi-field questions */}
              {(showContinue || showSkip) && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mt: 2.5 }}>
                  {showSkip && (
                    <Typography
                      component="button"
                      onClick={onAutoSubmit}
                      sx={{
                        fontSize: '13px', color: '#5F6B7A', fontWeight: 500,
                        cursor: 'pointer', background: 'none', border: 'none',
                        textDecoration: 'underline', '&:hover': { color: '#1E3A5F' },
                      }}
                    >
                      {t('mortgageWizard.nav.skip')}
                    </Typography>
                  )}
                  {showContinue && (
                    <Button
                      variant="contained"
                      onClick={handleContinue}
                      sx={{
                        bgcolor: '#1E3A5F', borderRadius: '10px', textTransform: 'none',
                        fontWeight: 600, px: 3, py: 1, fontSize: '14px',
                        boxShadow: '0 2px 8px rgba(30,58,95,0.20)',
                        transition: 'all 0.2s ease',
                        '&:hover': { bgcolor: '#1A3152', transform: 'translateY(-1px)', boxShadow: '0 4px 14px rgba(30,58,95,0.28)' },
                      }}
                    >
                      {isLast ? t('mortgageWizard.nav.submit') : t('mortgageWizard.nav.next')}
                    </Button>
                  )}
                </Box>
              )}

              {/* Insight bubble */}
              {newInsights.length > 0 && isFocused && (
                <InsightBubble insights={newInsights} />
              )}
            </Box>
        );
      })}
    </Box>
  );
};

export default QuestionFlow;
